import type { NativeFederationResult } from '@softarc/native-federation-orchestrator';
import { remoteManifest } from './remotes';

let resolveFederation: (federation: NativeFederationResult) => void;
const federationReady = new Promise<NativeFederationResult>((resolve) => {
    resolveFederation = resolve;
});

/**
 * Hands the initialised federation result to the app. Called by main-mfe.ts as soon as
 * initFederation resolves, before Angular bootstraps.
 *
 * The top-level loadRemoteModule exported by @angular-architects/native-federation can't be
 * used instead: it awaits a module-scoped promise that only that package's own initFederation
 * resolves, and we call the orchestrator's initFederation directly so the host gets native
 * import maps rather than es-module-shims' shim mode. Going through it would hang forever.
 */
export function setRemoteModuleLoader(federation: NativeFederationResult): void {
    resolveFederation(federation);
}

/**
 * Loads a module exposed by one of the remotes registered in main-mfe.ts's manifest. The
 * remote's code is fetched at this point, not at startup, so it stays off the critical path
 * until something actually needs it.
 *
 * initFederation only fetches each remote's remoteEntry.json once, at host bootstrap - a remote
 * that's unreachable at that moment is never registered for the rest of this page's lifetime,
 * so every later load attempt would otherwise fail instantly with no network request, even once
 * the remote comes back up. To self-heal that without forcing a full page reload, a failed load
 * re-registers the remote via initRemoteEntry (a genuine, uncached fetch of its remoteEntry.json)
 * and retries once before giving up.
 *
 * Rejects in non-MFE builds (main.ts), which never initialise federation at all.
 */
export async function loadRemoteModule<T>(remoteName: string, exposedModule: string): Promise<T> {
    if (!window.__isMfeBuild) {
        throw new Error(
            `Cannot load "${exposedModule}" from remote "${remoteName}": federation is only initialised by the MFE ` +
                `entry point (main-mfe.ts). Serve the host with "npm start", not a plain "nx serve web-v2".`
        );
    }

    const federation = await federationReady;
    try {
        return await federation.loadRemoteModule<T>(remoteName, exposedModule);
    } catch (error) {
        const remoteEntryUrl = remoteManifest()[remoteName];
        if (!remoteEntryUrl) {
            throw error;
        }
        await federation.initRemoteEntry(remoteEntryUrl, remoteName);
        return federation.loadRemoteModule<T>(remoteName, exposedModule);
    }
}
